import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchStoreBySlug, fetchStoreByDomain } from '../../features/store/storeSlice';
import { fetchCart } from '../../features/cart/cartSlice';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { GA4Provider } from '../../contexts/GA4Context';
import { StorefrontCurrencyProvider } from '../../contexts/StorefrontCurrencyContext';
import useStorefrontTheme, { StorefrontThemeProvider } from '../../hooks/useStorefrontTheme';
import Header from './Header';
import Footer from './Footer';
import StorefrontBottomNav from '../storefront/StorefrontBottomNav';
import StorefrontWhatsAppFloat from '../storefront/StorefrontWhatsAppFloat';
import ChatbotWidget from '../storefront/Chatbot/ChatbotWidget';
import PoweredByRevstar from '../PoweredByRevstar';
import CheckoutModal from '../checkout-modal/CheckoutModal';
import '../../styles/storefrontTheme.css';
import { getPlatformDomainName } from '../../config/platformConfig';
import visitTracker from '../../utils/visitTracker';
import { trackVisit, updateVisitDuration } from '../../features/visits/visitSlice';
import axiosInstance from '../../axiosConfig';
import { apiFetch } from '../../utils/api';

// Helper to get subdomain slug
const getSubdomainSlug = () => {
  const hostname = window.location.hostname;
  const platformDomain = getPlatformDomainName();
  const platformDomainWithoutPort = platformDomain.split(':')[0];
  const reservedSubdomains = ['admin', 'api', 'www', 'app', 'dashboard', 'cdn', 'static', 'marketplace'];
  const cleanHostname = hostname.replace(/^www\./, '');

  // LOCAL DEVELOPMENT: Handle *.localhost subdomains
  if (cleanHostname.endsWith('.localhost')) {
    const subdomain = cleanHostname.replace('.localhost', '');
    if (subdomain && !reservedSubdomains.includes(subdomain)) {
      console.log('[StorefrontLayout] Local dev subdomain detected:', subdomain);
      return subdomain;
    }
  }

  // Production: Handle platform subdomains (e.g., mystore.userevstar.com)
  if (cleanHostname.includes('.') && cleanHostname.endsWith(platformDomainWithoutPort)) {
    const subdomain = cleanHostname.substring(0, cleanHostname.length - platformDomainWithoutPort.length - 1);
    if (subdomain && !reservedSubdomains.includes(subdomain)) {
      return subdomain;
    }
  }
  return null;
};

// Helper to detect if current hostname is a custom domain
const isCustomDomain = () => {
  const hostname = window.location.hostname;
  const platformDomain = getPlatformDomainName();
  const platformDomainWithoutPort = platformDomain.split(':')[0];

  // Custom domain = not localhost, not vercel, not ending with platform domain
  const isCustom = !hostname.endsWith(platformDomainWithoutPort) &&
                   !hostname.includes('localhost') &&
                   !hostname.includes('127.0.0.1') &&
                   !hostname.includes('vercel.app');

  console.log('[StorefrontLayout] isCustomDomain check:', {
    hostname,
    platformDomain: platformDomainWithoutPort,
    isCustom
  });

  return isCustom;
};

const StorefrontPreloader = ({ storeName = 'Store' }) => {
  const displayName = storeName || 'Store';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f7f8fa',
      color: '#111827',
      fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    }}>
      <div style={{
        maxWidth: '1160px',
        margin: '0 auto',
        padding: '28px 20px 48px'
      }}>
        <div style={{
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              backgroundColor: '#111827',
              color: '#ffffff',
              display: 'grid',
              placeItems: 'center',
              fontSize: '0.95rem',
              fontWeight: 700
            }}>
              {initial}
            </div>
            <div style={{
              height: '14px',
              width: '132px',
              borderRadius: '999px',
              backgroundColor: '#e5e7eb'
            }} />
          </div>
          <div data-sf-preloader-nav style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {[72, 58, 68].map((width) => (
              <div
                key={width}
                style={{
                  height: '12px',
                  width,
                  borderRadius: '999px',
                  backgroundColor: '#e5e7eb'
                }}
              />
            ))}
          </div>
        </div>

        <main style={{
          minHeight: 'calc(100vh - 132px)',
          display: 'grid',
          alignContent: 'center',
          gap: '36px',
          padding: '48px 0'
        }}>
          <section style={{
            display: 'grid',
            justifyItems: 'center',
            textAlign: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '18px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 18px 48px rgba(15, 23, 42, 0.08)',
              display: 'grid',
              placeItems: 'center',
              color: '#111827',
              fontSize: '1.4rem',
              fontWeight: 750
            }}>
              {initial}
            </div>
            <div>
              <p style={{
                margin: 0,
                fontSize: '1.05rem',
                fontWeight: 700,
                letterSpacing: 0,
                color: '#111827'
              }}>
                {displayName}
              </p>
              <p style={{
                margin: '6px 0 0',
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 500
              }}>
                Preparing storefront
              </p>
            </div>
            <div style={{
              width: '168px',
              height: '3px',
              borderRadius: '999px',
              overflow: 'hidden',
              backgroundColor: '#e5e7eb'
            }}>
              <div style={{
                width: '42%',
                height: '100%',
                borderRadius: 'inherit',
                backgroundColor: '#111827',
                animation: 'sfCleanLoader 1.2s ease-in-out infinite'
              }} />
            </div>
          </section>

          <section data-sf-preloader-products style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '16px',
            opacity: 0.9
          }}>
            {[0, 1, 2, 3].map((item) => (
              <div
                key={item}
                style={{
                  minHeight: '176px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #edf0f3',
                  padding: '12px',
                  boxShadow: '0 10px 32px rgba(15, 23, 42, 0.04)'
                }}
              >
                <div style={{
                  aspectRatio: '4 / 3',
                  borderRadius: '6px',
                  background: 'linear-gradient(90deg, #eef1f4 0%, #f8fafc 50%, #eef1f4 100%)',
                  backgroundSize: '220% 100%',
                  animation: 'sfCleanSkeleton 1.4s ease-in-out infinite'
                }} />
                <div style={{
                  height: '10px',
                  width: '78%',
                  borderRadius: '999px',
                  backgroundColor: '#e5e7eb',
                  marginTop: '14px'
                }} />
                <div style={{
                  height: '10px',
                  width: '44%',
                  borderRadius: '999px',
                  backgroundColor: '#eef1f4',
                  marginTop: '10px'
                }} />
              </div>
            ))}
          </section>
        </main>
      </div>
      <style>{`
        @keyframes sfCleanLoader {
          0% { transform: translateX(-120%); }
          50% { transform: translateX(70%); }
          100% { transform: translateX(260%); }
        }
        @keyframes sfCleanSkeleton {
          0% { background-position: 120% 0; }
          100% { background-position: -120% 0; }
        }
        @media (max-width: 760px) {
          [data-sf-preloader-nav] { display: none; }
          [data-sf-preloader-products] {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

const StorefrontLayout = () => {
  const { slug: urlSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeState = useSelector(state => state.store);
  const store = storeState?.current;
  const status = storeState?.status;
  const [autoAuthProcessed, setAutoAuthProcessed] = useState(false);

  // Detect custom domain vs platform subdomain
  const customDomain = isCustomDomain();
  // Use URL slug or subdomain slug (only for platform subdomains)
  const slug = urlSlug || getSubdomainSlug();

  // Auto-authenticate customer from URL parameters (cross-domain SSO)
  useEffect(() => {
    const processAutoAuth = async () => {
      if (autoAuthProcessed) return;

      const params = new URLSearchParams(location.search);
      const authToken = params.get('auth');
      const customerId = params.get('customerId');
      const discountCode = params.get('discount');

      if (authToken && customerId) {
        console.log('[Auto-Auth] Processing customer authentication transfer...');

        try {
          // Verify token with backend - this will set an HTTP-only cookie
          const response = await axiosInstance.post(
            '/customer-auth/verify-token',
            { token: authToken, customerId }
          );

          if (response.data.success) {
            console.log('[Auto-Auth] Customer authenticated successfully:', response.data.customer);
            console.log('[Auto-Auth] Session cookie established via HTTP-only cookie');

            // If there's a discount code, store it in sessionStorage
            if (discountCode) {
              sessionStorage.setItem('pendingDiscountCode', discountCode);
              console.log('[Auto-Auth] Discount code saved for checkout:', discountCode);
            }

            // Clean up URL by removing auth params
            params.delete('auth');
            params.delete('customerId');
            const newSearch = params.toString();
            const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');

            // Replace URL without triggering navigation
            window.history.replaceState({}, '', newUrl);

            console.log('[Auto-Auth] Authentication complete, URL cleaned');
            console.log('[Auto-Auth] Customer can now proceed to checkout without re-authentication');
          } else {
            console.error('[Auto-Auth] Token verification failed:', response.data.error);
          }
        } catch (error) {
          console.error('[Auto-Auth] Error during auto-authentication:', error);
        }
      }

      setAutoAuthProcessed(true);
    };

    processAutoAuth();
  }, [location.search, autoAuthProcessed]);

  useEffect(() => {
    // For custom domains, fetch store by domain (backend looks up by hostname)
    if (customDomain) {
      console.log('[StorefrontLayout] Custom domain detected, fetching store by domain');
      dispatch(fetchStoreByDomain());
      // Cart will be fetched after store loads (see below)
    }
    // For platform subdomains, fetch store by slug
    else if (slug) {
      console.log('StorefrontLayout: Dispatching fetchStoreBySlug for slug:', slug);
      dispatch(fetchStoreBySlug(slug));
      // Cart will be fetched after store loads (see below)
    }
  }, [slug, customDomain, dispatch]);

  // Fetch cart AFTER store has loaded (fixes race condition for custom domains)
  useEffect(() => {
    if (store && status === 'succeeded') {
      console.log('[StorefrontLayout] Store loaded, fetching cart for:', store.slug);
      dispatch(fetchCart());
    }
  }, [store, status, dispatch]);

  // Capture deep link attribution token from URL on storefront load
  useEffect(() => {
    visitTracker.getDeepLinkToken();
  }, []);

  // Visit tracking - industry standard analytics
  const durationIntervalRef = useRef(null);

  useEffect(() => {
    // Only track visits when store is successfully loaded
    if (!store || status !== 'succeeded') return;

    const trackStoreVisit = async () => {
      try {
        console.log('[Visit Tracking] Initializing visit tracker for store:', store.name);

        // Collect visit data using the visitTracker utility
        const visitData = await visitTracker.trackVisit(
          store.ownerId,  // merchantId
          store.id,       // storeId
          window.location.pathname  // entryPage
        );

        // Send to backend via Redux
        await dispatch(trackVisit(visitData)).unwrap();

        console.log('[Visit Tracking] Visit tracked successfully');

        // Update visit duration every 30 seconds (industry standard)
        durationIntervalRef.current = setInterval(() => {
          const duration = visitTracker.updateDuration();
          const sessionId = visitTracker.sessionId;

          dispatch(updateVisitDuration({ sessionId, duration }));
          console.log('[Visit Tracking] Updated visit duration:', duration, 'seconds');
        }, 30000); // 30 seconds

      } catch (error) {
        console.error('[Visit Tracking] Error tracking visit:', error);
      }
    };

    trackStoreVisit();

    // Cleanup: Clear interval and update final duration on unmount
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);

        // Send final duration update
        const finalDuration = visitTracker.updateDuration();
        const sessionId = visitTracker.sessionId;
        dispatch(updateVisitDuration({ sessionId, duration: finalDuration }));

        console.log('[Visit Tracking] Final visit duration:', finalDuration, 'seconds');
      }
    };
  }, [store, status, dispatch]);

  // Update meta tags when store loads (for client-side rendering)
  // Note: This won't work for social crawlers - use the backend OG endpoint for sharing
  useEffect(() => {
    if (!store || status !== 'succeeded') return;

    const updateMetaTag = (selector, attribute, value) => {
      const element = document.querySelector(selector);
      if (element && value) {
        element.setAttribute(attribute, value);
      }
    };

    /* Storefront base URL (without path) — prefer the merchant's custom
       domain when one is configured. Used as a fallback for og:site_name
       and for building absolute URLs for child routes. */
    const platformDomain = getPlatformDomainName();
    const storeBaseUrl = store.customDomain
      ? `https://${store.customDomain}`
      : `https://${store.slug}.${platformDomain}`;

    /* The canonical URL for THIS route. Previously we hardcoded the store
       homepage URL into og:url on every route, which made Google bucket
       every non-home storefront URL as a duplicate of the homepage and
       refuse to index them. Build it from the live location so /shop,
       /product/x, /about, etc. each get their own canonical. */
    const pageUrl = `${storeBaseUrl}${location.pathname}${location.search || ''}`;

    // Build description
    const description = store.heroDescription || store.description || `Shop at ${store.name} - Browse products, place orders, and get fast delivery.`;

    // Build image URL (use logo, banner, or default)
    const imageUrl = store.logo || store.banner || 'https://revstar-media-production.s3.eu-north-1.amazonaws.com/website-assets/img/og-image.png';

    // Update document title
    document.title = store.name;

    // Update meta description
    updateMetaTag('meta[name="description"]', 'content', description);

    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', 'content', store.name);
    updateMetaTag('meta[property="og:description"]', 'content', description);
    updateMetaTag('meta[property="og:image"]', 'content', imageUrl);
    updateMetaTag('meta[property="og:url"]', 'content', pageUrl);
    updateMetaTag('meta[property="og:site_name"]', 'content', store.name);

    // Update Twitter Card tags
    updateMetaTag('meta[name="twitter:title"]', 'content', store.name);
    updateMetaTag('meta[name="twitter:description"]', 'content', description);
    updateMetaTag('meta[name="twitter:image"]', 'content', imageUrl);
    updateMetaTag('meta[name="twitter:url"]', 'content', pageUrl);

    /* Inject (or update) a <link rel="canonical"> for the current route
       so Google's JS-render pass can pick a per-URL canonical instead of
       falling back to og:url. Create the tag on first run since the
       static index.html doesn't ship one. */
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    // Update favicon if store has a logo
    if (store.logo) {
      const favicon32 = document.querySelector('link[rel="icon"][sizes="32x32"]');
      const favicon16 = document.querySelector('link[rel="icon"][sizes="16x16"]');
      const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');

      if (favicon32) favicon32.href = store.logo;
      if (favicon16) favicon16.href = store.logo;
      if (appleTouchIcon) appleTouchIcon.href = store.logo;
    }

    console.log('[StorefrontLayout] Meta tags updated for', pageUrl);
  }, [store, status, location.pathname, location.search]);

  // Debug logging
  console.log('StorefrontLayout - Status:', status, 'Store:', store, 'Slug:', slug);
  console.log('StorefrontLayout - Redux state:', storeState);

  // Show loading state while store is being fetched
  if (status === 'loading' || (!store && status !== 'failed')) {
    const slugForLoader = urlSlug || getSubdomainSlug();
    const loaderStoreName = slugForLoader
      ? slugForLoader.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : 'Store';

    return <StorefrontPreloader storeName={loaderStoreName} />;
  }

  // Show error state if fetch failed
  if (status === 'failed') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        color: '#64748b'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>Store Not Found</h2>
          <p>The store you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // Show offline message if store is not live
  if (store && !store.isLive) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        color: '#64748b'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '2rem'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 1.5rem',
            borderRadius: '50%',
            backgroundColor: '#fff3cd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="48" height="48" fill="none" stroke="#f59e0b" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h2 style={{
            color: '#f59e0b',
            marginBottom: '1rem',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            Store Temporarily Offline
          </h2>
          <p style={{
            marginBottom: '0.5rem',
            lineHeight: '1.6'
          }}>
            This store is currently offline. The merchant may be updating their inventory or making improvements.
          </p>
          <p style={{
            color: '#94a3b8',
            fontSize: '0.875rem'
          }}>
            Please check back later!
          </p>
        </div>
      </div>
    );
  }

  // Get theme name and custom colors from store
  const themeName = (store?.theme || 'MODERN').toUpperCase();
  const customColors = store?.themeConfig || {};

  return (
    <ThemeProvider themeName={themeName} customColors={customColors}>
      <GA4Provider store={store}>
        <StorefrontCurrencyProvider store={store}>
          {/* Single source of truth for the customer's light/dark
              preference. Without this, every consumer of
              `useStorefrontTheme` would own its own useState — a
              click on the header toggle would update the toggle's
              icon but leave the layout's data-storefront-theme
              attribute stale until refresh. */}
          <StorefrontThemeProvider
            storeKey={store?.id || store?.slug}
            defaultMode={store?.settings?.storefrontAppearance?.defaultMode}
            allowVisitorOverride={store?.settings?.storefrontAppearance?.showSwitcher}
          >
            <StorefrontContent store={store} />
          </StorefrontThemeProvider>
        </StorefrontCurrencyProvider>
      </GA4Provider>
    </ThemeProvider>
  );
};

// Announcement bar component (replaces AdvertiseBlurb)
const AnnouncementBar = ({ store, theme, campaigns = [] }) => {
  const [dismissed, setDismissed] = React.useState(() => {
    return sessionStorage.getItem('sf_announcement_dismissed') === 'true';
  });
  const [adInfo, setAdInfo] = React.useState(null);

  const announcement = store?.settings?.announcement;
  const activeSale = Array.isArray(campaigns) ? campaigns.find(c => c?.showOnStorefront && c?.placement === 'TOP_BAR') : null;
  const hasAnnouncement = announcement?.enabled && announcement?.text;
  const hasSaleAnnouncement = !!activeSale;

  // Check if we're on a subdomain (for ad fallback)
  const isOnSubdomain = React.useMemo(() => {
    const hostname = window.location.hostname;
    const platformDomain = getPlatformDomainName().split(':')[0];
    return hostname.includes('.') &&
           hostname.endsWith(platformDomain) &&
           !hostname.startsWith('www.');
  }, []);

  // Fallback: fetch ad info if no announcement configured
  React.useEffect(() => {
    if (hasAnnouncement || hasSaleAnnouncement) return;

    const fetchAdInfo = async () => {
      try {
        const response = await axiosInstance.get(
          `/storefront/ads/${store.slug}/advertise-info`
        );
        if (response.data.success && response.data.available) {
          setAdInfo(response.data);
        }
      } catch (error) {
        // Settings not available or ads not enabled
      }
    };

    if (store?.slug) {
      fetchAdInfo();
    }
  }, [store?.slug, hasAnnouncement, hasSaleAnnouncement]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem('sf_announcement_dismissed', 'true');
    setDismissed(true);
    // Notify hero-flush consumers (HomePage / AboutPage) so they can
    // re-evaluate and lift the nav over the hero now that the bar is
    // gone. sessionStorage doesn't fire 'storage' events same-tab.
    window.dispatchEvent(new CustomEvent('sf:announcement-dismissed'));
  };

  // Render announcement bar
  if ((hasSaleAnnouncement || hasAnnouncement) && !dismissed) {
    const bgColor = hasSaleAnnouncement ? '#111827' : (announcement.backgroundColor || '#1a1a2e');
    const txtColor = hasSaleAnnouncement ? '#ffffff' : (announcement.textColor || '#ffffff');
    const type = hasSaleAnnouncement ? 'static' : (announcement.type || 'static');
    const speed = announcement?.speed || 30;
    const announcementText = hasSaleAnnouncement
      ? `${activeSale.badgeText || 'SALE'}: ${activeSale.title}${activeSale.subtitle ? ` - ${activeSale.subtitle}` : ''}`
      : announcement.text;

    return (
      <>
        <div style={{
          position: 'relative',
          backgroundColor: bgColor,
          color: txtColor,
          padding: '10px 40px 10px 16px',
          fontSize: '13px',
          fontWeight: '500',
          overflow: 'hidden',
          whiteSpace: type === 'marquee' ? 'nowrap' : 'normal',
          textAlign: type === 'static' ? 'center' : 'left'
        }}>
          {type === 'marquee' ? (
            <div style={{
              display: 'inline-block',
              animation: `sfMarquee ${speed}s linear infinite`
            }}>
              <span style={{ paddingRight: '4rem' }}>{announcementText}</span>
              <span style={{ paddingRight: '4rem' }}>{announcementText}</span>
            </div>
          ) : (
            <span>{announcementText}</span>
          )}
          <button
            onClick={handleDismiss}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              color: txtColor,
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 1,
              fontSize: '16px',
              opacity: 0.7
            }}
            aria-label="Dismiss announcement"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <style>{`
          @keyframes sfMarquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </>
    );
  }

  // Fallback: render ad blurb if available
  if (!adInfo || !adInfo.available) return null;

  const handleAdClick = () => {
    if (adInfo.hasPricing) {
      const pricingPath = isOnSubdomain ? '/advertise' : `/s/${store.slug}/advertise`;
      window.open(pricingPath, '_blank');
    } else if (adInfo.contactEmail) {
      window.location.href = `mailto:${adInfo.contactEmail}?subject=Advertising Inquiry for ${store.name || 'your store'}`;
    }
  };

  return (
    <div
      onClick={handleAdClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '10px 16px',
        fontSize: '13px',
        fontWeight: '500',
        color: theme.colors.primary,
        backgroundColor: theme.colors.backgroundAlt || '#f8fafc',
        borderBottom: `1px solid ${theme.colors.border || '#e5e7eb'}`,
        cursor: (adInfo.hasPricing || adInfo.contactEmail) ? 'pointer' : 'default',
        transition: 'background-color 0.2s ease'
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
      <span>{adInfo.blurbText || 'Advertise with us!'}</span>
      {(adInfo.hasPricing || adInfo.contactEmail) && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      )}
    </div>
  );
};

// Merchant Storefront Exit Popup - Uses store.settings.newsletterPopup (controlled by merchant)
const NewsletterModal = ({ store, theme }) => {
  const [showModal, setShowModal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Get settings from store.settings.newsletterPopup (configured by merchant in their dashboard)
  const settings = store?.settings?.newsletterPopup || {};
  const isEnabled = settings.enabled !== false; // Default to enabled if not set

  // Show popup after delay
  useEffect(() => {
    if (!isEnabled) return;

    const cookieKey = 'sf_popup_shown_' + store?.slug;
    const alreadyShown = sessionStorage.getItem(cookieKey);
    const showOncePerSession = settings.showOncePerSession !== false;
    if (showOncePerSession && alreadyShown) return;

    const isMobile = window.innerWidth < 768;
    const showOnMobile = settings.showOnMobile !== false;
    if (isMobile && !showOnMobile) return;

    const delay = (settings.delaySeconds || 10) * 1000;
    const timer = setTimeout(() => {
      setIsAnimating(true);
      setShowModal(true);
      if (showOncePerSession) {
        sessionStorage.setItem(cookieKey, 'true');
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [isEnabled, settings, store?.slug]);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => setShowModal(false), 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Submit to merchant's store newsletter endpoint
      await axiosInstance.post('/newsletter/subscribe', {
        email,
        firstName: settings.showNameField ? name : undefined,
        phone: settings.showPhoneField ? phone : undefined,
        source: 'exit_popup',
        storeSlug: store.slug
      });
      setSuccess(true);
      setTimeout(() => handleDismiss(), 3000);
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyDiscountCode = () => {
    if (settings.discountCode) {
      navigator.clipboard.writeText(settings.discountCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!showModal || !isEnabled) return null;

  // Styling variables from merchant settings
  const primaryColor = settings.primaryColor || theme?.colors?.primary || '#0d7463';
  const bgColor = settings.backgroundColor || '#ffffff';
  const textColor = settings.textColor || '#1e293b';
  const radius = parseInt(settings.borderRadius) || 16;
  const layout = settings.layout || 'full';
  const hasImage = settings.imageUrl && (layout === 'split-left' || layout === 'split-right');
  const isSplit = layout === 'split-left' || layout === 'split-right';

  // Responsive
  const screenWidth = window.innerWidth;
  const isMobile = screenWidth < 640;

  // Animation styles
  const getAnimationStyle = () => {
    const base = { transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' };
    if (!isAnimating) {
      switch (settings.animation) {
        case 'slide-up': return { ...base, opacity: 0, transform: 'translateY(40px)' };
        case 'scale': return { ...base, opacity: 0, transform: 'scale(0.9)' };
        default: return { ...base, opacity: 0 };
      }
    }
    return { ...base, opacity: 1, transform: 'translateY(0) scale(1)' };
  };

  // Button style variants
  const getButtonStyle = () => {
    const base = {
      width: '100%',
      padding: isMobile ? '14px 20px' : '12px 24px',
      fontSize: isMobile ? '16px' : '0.95rem',
      fontWeight: '600',
      cursor: loading ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
      borderRadius: settings.buttonStyle === 'rounded' ? '50px' : '8px'
    };
    switch (settings.buttonStyle) {
      case 'outline':
        return { ...base, backgroundColor: 'transparent', color: primaryColor, border: `2px solid ${primaryColor}` };
      default:
        return { ...base, backgroundColor: primaryColor, color: '#ffffff' };
    }
  };

  // Input styling
  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: isMobile ? '16px' : '0.95rem',
    marginBottom: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#f8fafc',
    color: textColor,
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };

  // Modal width based on layout
  const getModalWidth = () => {
    if (isMobile) return '94%';
    if (isSplit) return '800px';
    if (layout === 'minimal') return '380px';
    return '440px';
  };

  // Get content text
  const headline = settings.headline || 'Stay in the loop!';
  const subtext = settings.subtext || 'Get exclusive deals and updates';
  const buttonText = settings.buttonText || 'Subscribe';
  const dismissText = settings.dismissText || 'No thanks';
  const successMessage = settings.successMessage || "You're all set!";

  return (
    <>
      <style>{`
        @keyframes sfPopupSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10001,
          backgroundColor: isAnimating ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0)',
          backdropFilter: isAnimating ? 'blur(4px)' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '1rem' : '2rem',
          transition: 'all 0.3s ease'
        }}
        onClick={handleDismiss}
      >
        <div
          style={{
            backgroundColor: bgColor,
            borderRadius: `${radius}px`,
            maxWidth: getModalWidth(),
            width: '100%',
            position: 'relative',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: isSplit && !isMobile ? (layout === 'split-left' ? 'row' : 'row-reverse') : 'column',
            overflow: 'hidden',
            maxHeight: isMobile ? '90vh' : '85vh',
            ...getAnimationStyle()
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: hasImage && !isMobile ? 'rgba(255,255,255,0.95)' : 'transparent',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              color: '#64748b',
              zIndex: 10,
              transition: 'background 0.2s, color 0.2s'
            }}
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Image section for split layouts */}
          {hasImage && (
            <div style={{
              flex: isMobile ? '0 0 180px' : '0 0 50%',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#f1f5f9',
              minHeight: isMobile ? '180px' : '400px'
            }}>
              <img
                src={settings.imageUrl}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              {settings.imageOverlay && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 100%)'
                }} />
              )}
            </div>
          )}

          {/* Form content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'auto' }}>
            <div style={{ padding: isSplit && !isMobile ? '2.5rem' : (isMobile ? '1.5rem' : '2rem') }}>
              {success ? (
                // Success state
                <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.25rem'
                  }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: textColor, marginBottom: '0.5rem' }}>
                    {successMessage}
                  </h3>
                  {settings.showDiscount && settings.discountCode && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Your discount code:</p>
                      <button
                        onClick={copyDiscountCode}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 20px',
                          backgroundColor: '#f1f5f9',
                          border: '2px dashed #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          fontFamily: 'monospace',
                          letterSpacing: '1px',
                          color: primaryColor,
                          cursor: 'pointer'
                        }}
                      >
                        {settings.discountCode}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                      </button>
                      {copied && <p style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: '0.5rem' }}>Copied!</p>}
                    </div>
                  )}
                </div>
              ) : (
                // Form state
                <>
                  {!isSplit && (
                    <div style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>
                  )}

                  <h2 style={{
                    fontSize: isMobile ? '1.5rem' : '1.75rem',
                    fontWeight: '800',
                    color: textColor,
                    marginBottom: '0.5rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    textAlign: isSplit ? 'left' : 'center'
                  }}>
                    {headline}
                  </h2>

                  <p style={{
                    color: '#64748b',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    marginBottom: '1.25rem',
                    textAlign: isSplit ? 'left' : 'center'
                  }}>
                    {subtext}
                  </p>

                  {/* Discount badge */}
                  {settings.showDiscount && settings.discountCode && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      backgroundColor: `${primaryColor}15`,
                      borderRadius: '6px',
                      marginBottom: '1.25rem'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={primaryColor} stroke="none">
                        <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>
                      </svg>
                      <span style={{ fontSize: '0.85rem', color: primaryColor, fontWeight: '600' }}>
                        {settings.discountLabel || 'Use code at checkout:'}
                      </span>
                      <code style={{
                        padding: '2px 8px',
                        backgroundColor: primaryColor,
                        color: '#fff',
                        borderRadius: '4px',
                        fontWeight: '700',
                        fontSize: '0.8rem'
                      }}>
                        {settings.discountCode}
                      </code>
                    </div>
                  )}

                  {error && (
                    <div style={{
                      padding: '10px 14px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}>
                      <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    {settings.showNameField && (
                      <input
                        type="text"
                        placeholder={settings.namePlaceholder || 'Your name'}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                      />
                    )}

                    <input
                      type="email"
                      required
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                    />

                    {settings.showPhoneField && (
                      <input
                        type="tel"
                        placeholder={settings.phonePlaceholder || 'Phone number'}
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                      />
                    )}

                    {/* Subscribe + No Thanks buttons side by side */}
                    <div style={{
                      display: 'flex',
                      gap: '0.75rem',
                      alignItems: 'center'
                    }}>
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          ...getButtonStyle(),
                          flex: 1,
                          opacity: loading ? 0.7 : 1
                        }}
                      >
                        {loading ? (
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" style={{ animation: 'sfPopupSpin 1s linear infinite' }}>
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="32" strokeLinecap="round" />
                            </svg>
                            Processing...
                          </span>
                        ) : buttonText}
                      </button>
                      <button
                        type="button"
                        onClick={handleDismiss}
                        style={{
                          padding: '12px 20px',
                          background: 'none',
                          border: '1.5px solid #e2e8f0',
                          borderRadius: settings.buttonStyle === 'rounded' ? '50px' : '8px',
                          color: '#64748b',
                          fontSize: isMobile ? '16px' : '0.95rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {dismissText}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Separate component to access theme context
const StorefrontContent = ({ store }) => {
  const { useTheme } = require('../../contexts/ThemeContext');
  const theme = useTheme();
  // Customer's dark/light preference for THIS storefront. Independent of
  // the merchant's brand colors (theme.colors.primary stays put); only
  // neutral surfaces swap in dark mode. See styles/storefrontTheme.css.
  const { resolvedTheme } = useStorefrontTheme();
  const campaigns = useSelector(state => state.store?.campaigns || []);
  const isDark = resolvedTheme === 'dark';
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [premiumThemeConfig, setPremiumThemeConfig] = useState(null);
  const [themeCheckComplete, setThemeCheckComplete] = useState(false);
  // Sales Tools — Phase 3.2. CheckoutLinkLanding opens the modal directly
  // via the onOpenCart callback in the outlet context (declared below) and
  // stashes a customer-info prefill in sessionStorage that the CheckoutModal
  // consumes on open. No mount-time effect needed here.

  // Fetch premium theme config for theme builder pages
  useEffect(() => {
    const fetchPremiumTheme = async () => {
      if (!store?.slug) {
        setThemeCheckComplete(true);
        return;
      }

      try {
        const res = await fetch(`/api/themes/storefront/${store.slug}/theme`);
        const data = await res.json();
        if (data.success && data.themeConfig?.themeId) {
          setPremiumThemeConfig(data.themeConfig);
        }
      } catch {
        // Store doesn't have premium theme - that's fine
      } finally {
        setThemeCheckComplete(true);
      }
    };

    fetchPremiumTheme();
  }, [store?.slug]);

  // Create themeStyles object for backward compatibility
  const themeStyles = {
    primary: theme.colors.primary,
    primaryHover: theme.colors.primaryHover,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
    background: theme.colors.background,
    backgroundAlt: theme.colors.backgroundAlt,
    text: theme.colors.text,
    textMuted: theme.colors.textMuted,
    textLight: theme.colors.textMuted, // Alias for compatibility
    border: theme.colors.border,
    success: theme.colors.success,
    error: theme.colors.error,
    warning: theme.colors.warning,
    borderRadius: theme.borderRadius.md,
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    fontPrimary: theme.fonts.primary,
    fontHeading: theme.fonts.heading,
    spacing: theme.spacing,
    buttonStyles: theme.buttonStyles
  };

  // Dark-mode override: swap neutral surfaces (background, text, border)
  // for our dark tokens while preserving the merchant's brand colors
  // (primary, accent). The inline-styled Header/Footer/page chrome read
  // from themeStyles, so this object is the single point of swap — no
  // need to convert every inline hex.
  const DARK_NEUTRALS = {
    background:    '#0b0f17',
    backgroundAlt: '#131825',
    text:          '#f3f4f6',
    textMuted:     '#9ca3af',
    textLight:     '#9ca3af',
    muted:         '#9ca3af',
    border:        '#1f2937',
  };
  const ATELIER_DARK_NEUTRALS = {
    background:    '#20231f',
    backgroundAlt: '#20231f',
    text:          '#f6f1e8',
    textMuted:     '#c8c0b4',
    textLight:     '#c8c0b4',
    muted:         '#c8c0b4',
    border:        '#394037',
  };
  const GALLERY_DARK_NEUTRALS = {
    primary:       '#cfff22',
    secondary:     '#ffffff',
    accent:        '#cfff22',
    background:    '#0a0a0a',
    backgroundAlt: '#191919',
    text:          '#ffffff',
    textMuted:     '#bcbcbc',
    textLight:     '#bcbcbc',
    muted:         '#bcbcbc',
    border:        '#ffffff',
  };
  const defaultDarkNeutrals = premiumThemeConfig?.theme?.slug === 'atelier'
    ? ATELIER_DARK_NEUTRALS
    : premiumThemeConfig?.theme?.slug === 'gallery'
      ? GALLERY_DARK_NEUTRALS
      : DARK_NEUTRALS;
  const configuredDarkColors = premiumThemeConfig?.globalOverrides?.darkColors
    || premiumThemeConfig?.theme?.globalSettings?.darkColors
    || {};
  const darkNeutrals = { ...defaultDarkNeutrals, ...configuredDarkColors };
  const effectiveThemeStyles = isDark
    ? {
        ...themeStyles,
        ...darkNeutrals,
        shadow: '0 1px 3px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
      }
    : themeStyles;

  // Premium themes provide their own `globalOverrides.colors` and
  // `theme.globalSettings.colors` blobs that take precedence over
  // `themeStyles` in the page-level helper variables (ShopPage,
  // CategoryPage, ContactPage, etc.). In dark mode we override the
  // neutral keys on both paths so the page chrome flips even when a
  // premium theme is active. Brand colors (primary, secondary, accent)
  // pass through unchanged — the merchant's identity stays put.
  const effectivePremiumThemeConfig = isDark && premiumThemeConfig
    ? {
        ...premiumThemeConfig,
        globalOverrides: {
          ...premiumThemeConfig.globalOverrides,
          colors: {
            ...premiumThemeConfig.globalOverrides?.colors,
            ...(premiumThemeConfig?.theme?.slug === 'gallery' ? { primary: '#cfff22', secondary: '#ffffff', accent: '#cfff22' } : {}),
            ...configuredDarkColors,
            text:          darkNeutrals.text,
            background:    darkNeutrals.background,
            backgroundAlt: darkNeutrals.backgroundAlt,
            border:        darkNeutrals.border,
            muted:         darkNeutrals.muted,
          },
        },
        theme: premiumThemeConfig.theme
          ? {
              ...premiumThemeConfig.theme,
              globalSettings: {
                ...premiumThemeConfig.theme.globalSettings,
                colors: {
                  ...premiumThemeConfig.theme.globalSettings?.colors,
                  ...(premiumThemeConfig?.theme?.slug === 'gallery' ? { primary: '#cfff22', secondary: '#ffffff', accent: '#cfff22' } : {}),
                  ...configuredDarkColors,
                  text:          darkNeutrals.text,
                  background:    darkNeutrals.background,
                  backgroundAlt: darkNeutrals.backgroundAlt,
                  border:        darkNeutrals.border,
                  muted:         darkNeutrals.muted,
                },
              },
            }
          : premiumThemeConfig.theme,
      }
    : premiumThemeConfig;
  const activeThemeColors = effectivePremiumThemeConfig?.globalOverrides?.colors
    || effectivePremiumThemeConfig?.theme?.globalSettings?.colors
    || {};

  // Check if merchant can remove badge (based on feature access from backend)
  const canRemoveBadge = store?.canRemoveBadge || false;

  // Wait for theme check to complete before rendering to prevent flash of default styles
  if (!themeCheckComplete) {
    const loaderStoreName = store?.name || store?.slug?.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Store';

    return <StorefrontPreloader storeName={loaderStoreName} />;
  }

  return (
    <div
      data-storefront-theme={resolvedTheme}
      className={premiumThemeConfig?.theme?.slug ? `storefront-content--${premiumThemeConfig.theme.slug}` : undefined}
      style={{
        '--theme-primary': activeThemeColors.primary || effectiveThemeStyles.primary,
        '--theme-secondary': activeThemeColors.secondary || effectiveThemeStyles.secondary,
        '--theme-accent': activeThemeColors.accent || effectiveThemeStyles.accent,
        '--theme-background': activeThemeColors.background || effectiveThemeStyles.background,
        '--theme-text': activeThemeColors.text || effectiveThemeStyles.text,
        '--theme-muted': activeThemeColors.muted || effectiveThemeStyles.textMuted,
        '--theme-dark-primary': configuredDarkColors.primary || defaultDarkNeutrals.primary || effectiveThemeStyles.primary,
        '--theme-dark-secondary': configuredDarkColors.secondary || defaultDarkNeutrals.secondary || effectiveThemeStyles.secondary,
        '--theme-dark-accent': configuredDarkColors.accent || defaultDarkNeutrals.accent || effectiveThemeStyles.accent,
        '--theme-dark-background': configuredDarkColors.background || defaultDarkNeutrals.background,
        '--theme-dark-text': configuredDarkColors.text || defaultDarkNeutrals.text,
        '--theme-dark-muted': configuredDarkColors.muted || defaultDarkNeutrals.muted,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: effectiveThemeStyles.background,
        fontFamily: theme.fonts.primary,
        color: effectiveThemeStyles.text,
      }}
    >
      <AnnouncementBar store={store} theme={theme} campaigns={campaigns} />
      <Header store={store} theme={theme} themeStyles={effectiveThemeStyles} onOpenCart={() => setShowCheckoutModal(true)} />

      <main style={{ flex: 1 }}>
        <Outlet context={{ store, theme, themeStyles: effectiveThemeStyles, premiumThemeConfig: effectivePremiumThemeConfig, onOpenCart: () => setShowCheckoutModal(true) }} />
      </main>

      <Footer store={store} theme={theme} themeStyles={effectiveThemeStyles} />

      {/* Mobile floating bottom dock — hidden on desktop via CSS. The
          spacer below reserves room so footer/page bottom isn't masked
          by the dock on small screens. */}
      <StorefrontBottomNav store={store} onOpenCart={() => setShowCheckoutModal(true)} />
      <div className="sf-bottom-nav-spacer" aria-hidden="true" />

      {/* Side-tucked WhatsApp affordance (every page, not just Home).
          Renders nothing if the merchant doesn't have a number. */}
      <StorefrontWhatsAppFloat store={store} />

      {/* AI chatbot widget — bottom-right launcher + slide-up panel.
          Renders nothing if `sales.storefront_chatbot` is disabled or the
          per-store setting is off. Auto-opens after the configured delay. */}
      <ChatbotWidget store={store} />

      {/* Powered by Revstar badge - hidden if merchant has branding.remove_badge feature */}
      <PoweredByRevstar canRemoveBadge={canRemoveBadge} storeSlug={store?.slug} />

      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        storeSlug={store?.slug}
        storeData={store}
        themeSlug={premiumThemeConfig?.theme?.slug}
      />

      <NewsletterModal store={store} theme={theme} />
    </div>
  );
};

export default StorefrontLayout;
